/**
 * Base Page class - represents a collection of UI components
 * Each page contains components (buttons, controls) and has navigation capabilities
 */
class Page {
  constructor(name, id = null) {
    this.id = id || `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.name = name;
    this.components = [];
    this.canvasProperties = {
      backgroundColor: '#383838',
      backgroundType: 'solid',
      activeColorMode: 'default',
      colorModes: {
        default: {
          buttonBackgroundColor: '#004C97',
          buttonTextColor: '#FFFFFF'
        },
        highContrast: {
          buttonBackgroundColor: '#FFFF00',
          buttonTextColor: '#000000'
        },
        lightBackgrounds: {
          buttonBackgroundColor: '#E8F4F8',
          buttonTextColor: '#000000'
        }
      },
      activeFontSize: 18,
      fontSizes: {
        small: 16,
        medium: 18,
        large: 24,
        extraLarge: 30
      },
      activeFontFamily: 'Arial',
      dayNightConfig: {
        enabled: false,
        dayTime: '08:00',
        nightTime: '20:00',
        dayFontSize: 18,
        nightFontSize: 24
      }
    };
    
    // WCAG 2.2.1 Timing Adjustable compliance settings
    this.timeoutSettings = {
      enabled: false,                    // Enable/disable auto page back
      delay: 0,                         // Timeout delay in seconds (0 = no timeout)
      warningTime: 20,                  // Warning time before timeout (seconds)
      allowExtension: true,             // Allow user to extend time
      extensionTime: 600,               // Extension time in seconds (10 minutes)
      targetPage: null,                 // Page to navigate to on timeout (null = previous page)
      lastActivity: null,               // Track last user activity (runtime only)
      wcagCompliant: true               // WCAG 2.2.1 compliance flag
    };
    
    this.metadata = {
      createdAt: new Date(),
      modifiedAt: new Date()
    };
  }

  /**
   * Add a component to this page
   * @param {Object} component - The component to add
   */
  addComponent(component) {
    // Ensure component has an ID
    if (!component.id) {
      component.id = `comp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Log if accessibility metadata is missing
    if (!component.accessibility || !component.accessibility.ariaLabel || !component.accessibility.role) {
      console.warn(
        `[UIA] Component "${component.name || component.id}" added to page "${this.name}" without proper accessibility metadata (ariaLabel, role, etc.).`
      );
    }
    
    this.components.push(component);
    
    // Ensure metadata exists before setting modifiedAt
    if (!this.metadata) {
      this.metadata = {
        createdAt: new Date(),
        modifiedAt: new Date()
      };
    } else {
      this.metadata.modifiedAt = new Date();
    }
    return component.id;
  }

  /**
   * Remove a component from this page
   * @param {string} componentId - The ID of the component to remove
   */
  removeComponent(componentId) {
    const index = this.components.findIndex(comp => comp.id === componentId);
    if (index !== -1) {
      this.components.splice(index, 1);
      
      // Ensure metadata exists before setting modifiedAt
      if (!this.metadata) {
        this.metadata = {
          createdAt: new Date(),
          modifiedAt: new Date()
        };
      } else {
        this.metadata.modifiedAt = new Date();
      }
      return true;
    }
    return false;
  }

  /**
   * Update a component on this page
   * @param {string} componentId - The ID of the component to update
   * @param {Object} updates - The updates to apply
   */
  updateComponent(componentId, updates) {
    const component = this.components.find(comp => comp.id === componentId);
    if (component) {
      Object.assign(component, updates);
      
      // Ensure metadata exists before setting modifiedAt
      if (!this.metadata) {
        this.metadata = {
          createdAt: new Date(),
          modifiedAt: new Date()
        };
      } else {
        this.metadata.modifiedAt = new Date();
      }
      return true;
    }
    return false;
  }

  /**
   * Get a component by ID
   * @param {string} componentId - The ID of the component to get
   */
  getComponent(componentId) {
    return this.components.find(comp => comp.id === componentId);
  }

  /**
   * Get all components on this page
   */
  getComponents() {
    return [...this.components];
  }

  /**
   * Get all components on this page (legacy method, kept for compatibility)
   */
  getAllComponents() {
    return [...this.components];
  }

  /**
   * Clear all components from this page
   */
  clearComponents() {
    this.components = [];
    
    // Ensure metadata exists before setting modifiedAt
    if (!this.metadata) {
      this.metadata = {
        createdAt: new Date(),
        modifiedAt: new Date()
      };
    } else {
      this.metadata.modifiedAt = new Date();
    }
  }

  /**
   * Get timeout settings for this page
   */
  getTimeoutSettings() {
    return { ...this.timeoutSettings };
  }

  /**
   * Update timeout settings for this page
   * @param {Object} settings - Timeout settings to update
   */
  updateTimeoutSettings(settings) {
    console.log(`⏱️ Page "${this.name}" updateTimeoutSettings called:`, settings);
    console.log(`⏱️ Before update:`, this.timeoutSettings);
    
    this.timeoutSettings = {
      ...this.timeoutSettings,
      ...settings
    };
    
    // Validate settings for WCAG compliance
    if (this.timeoutSettings.enabled && this.timeoutSettings.delay > 0) {
      // Ensure minimum warning time for WCAG compliance
      if (this.timeoutSettings.warningTime < 20) {
        this.timeoutSettings.warningTime = 20;
        console.warn(`⚠️ Warning time increased to 20 seconds for WCAG 2.2.1 compliance`);
      }
      
      // Ensure extension is available for WCAG compliance
      if (!this.timeoutSettings.allowExtension) {
        console.warn(`⚠️ Extension should be enabled for WCAG 2.2.1 compliance`);
      }
    }
    
    console.log(`⏱️ After update:`, this.timeoutSettings);
    
    // Update metadata
    if (!this.metadata) {
      this.metadata = {
        createdAt: new Date(),
        modifiedAt: new Date()
      };
    } else {
      this.metadata.modifiedAt = new Date();
    }
  }

  /**
   * Check if page has WCAG compliant timeout settings
   */
  isWCAGTimeoutCompliant() {
    if (!this.timeoutSettings.enabled || this.timeoutSettings.delay === 0) {
      return true; // No timeout = compliant
    }
    
    return (
      this.timeoutSettings.warningTime >= 20 &&     // Minimum 20 second warning
      this.timeoutSettings.allowExtension &&        // Must allow extension
      this.timeoutSettings.extensionTime >= 600     // Minimum 10 minute extension
    );
  }

  /**
   * Get canvas properties for this page
   */
  getCanvasProperties() {
    return { ...this.canvasProperties };
  }

  /**
   * Update canvas properties for this page
   * @param {Object} properties - Canvas properties to update
   */
  updateCanvasProperties(properties) {
    console.log(`🎨 Page "${this.name}" updateCanvasProperties called:`, properties);
    console.log(`🎨 Before update:`, this.canvasProperties);
    this.canvasProperties = {
      ...this.canvasProperties,
      ...properties
    };
    console.log(`🎨 After update:`, this.canvasProperties);
    
    // Ensure metadata exists before setting modifiedAt
    if (!this.metadata) {
      this.metadata = {
        createdAt: new Date(),
        modifiedAt: new Date()
      };
    } else {
      this.metadata.modifiedAt = new Date();
    }
  }

  /**
   * Get page information
   */
  getPageInfo() {
    return {
      id: this.id,
      name: this.name,
      componentCount: this.components.length,
      metadata: this.metadata
    };
  }

  /**
   * Serialize page to JSON
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      components: this.components,
      canvasProperties: this.canvasProperties,
      timeoutSettings: this.timeoutSettings,
      metadata: this.metadata
    };
  }

  /**
   * Create page from JSON data
   * @param {Object} data - The JSON data to restore from
   */
  static fromJSON(data) {
    const page = new Page(data.name, data.id);
    page.components = data.components || [];
    
    // Initialize canvas properties with defaults for color modes
    const defaultCanvasProps = { 
      backgroundColor: '#383838', 
      backgroundType: 'solid',
      activeColorMode: 'default',
      colorModes: {
        default: {
          buttonBackgroundColor: '#004C97',
          buttonTextColor: '#FFFFFF'
        },
        highContrast: {
          buttonBackgroundColor: '#FFFF00',
          buttonTextColor: '#000000'
        },
        lightBackgrounds: {
          buttonBackgroundColor: '#E8F4F8',
          buttonTextColor: '#000000'
        }
      },
      activeFontSize: 18,
      fontSizes: {
        small: 16,
        medium: 18,
        large: 24,
        extraLarge: 30
      },
      activeFontFamily: 'Arial',
      dayNightConfig: {
        enabled: false,
        dayTime: '08:00',
        nightTime: '20:00',
        dayFontSize: 18,
        nightFontSize: 24
      }
    };
    
    // Merge with existing canvas properties
    page.canvasProperties = {
      ...defaultCanvasProps,
      ...data.canvasProperties
    };
    
    // Ensure colorModes exists even if not in saved data (backwards compatibility)
    if (!page.canvasProperties.colorModes) {
      page.canvasProperties.colorModes = defaultCanvasProps.colorModes;
    }
    
    // Ensure activeColorMode exists
    if (!page.canvasProperties.activeColorMode) {
      page.canvasProperties.activeColorMode = 'default';
    }
    
    // Restore timeout settings with defaults for backwards compatibility
    page.timeoutSettings = {
      enabled: false,
      delay: 0,
      warningTime: 20,
      allowExtension: true,
      extensionTime: 600,
      targetPage: null,
      lastActivity: null,
      wcagCompliant: true,
      ...data.timeoutSettings // Override with saved values if they exist
    };
    
    page.metadata = data.metadata || {
      createdAt: new Date(),
      modifiedAt: new Date()
    };
    return page;
  }
}

export default Page;